import { createSelector } from 'reselect'
import { create } from 'zustand'

const STORAGE_VERSION = 5 // Increment when making breaking changes

export type FiltersStoreState<T extends Record<string, unknown>> = {
    areFiltersOpen: boolean
    setAreFiltersOpen: (areFiltersOpen: boolean) => void
    activeFilters: T
    resetActiveFilters: () => void
    setActiveFilters: (filters: T) => void
    updateActiveFilter: <K extends keyof T>(key: K, value: T[K]) => void
}

const createFiltersStore = <T extends Record<string, unknown>>(
    storeKey: string,
    defaultFilters: T
) => {
    return create<FiltersStoreState<T>>((set) => {
        const storedData = getLocalStorageItem(storeKey)
        let initialFilters: T

        // Handle version mismatch
        if (storedData && storedData.version === STORAGE_VERSION) {
            initialFilters = storedData.filters as T
        } else {
            setLocalStorageItem(storeKey, defaultFilters)
            initialFilters = defaultFilters
        }

        return {
            areFiltersOpen: false,
            setAreFiltersOpen: (areFiltersOpen) => {
                set({ areFiltersOpen })
            },
            activeFilters: initialFilters,
            resetActiveFilters: () => {
                set(() => {
                    setLocalStorageItem(storeKey, defaultFilters)
                    return { activeFilters: defaultFilters }
                })
            },
            setActiveFilters: (filters) => {
                set((state) => {
                    const updatedFilters = {
                        ...state.activeFilters,
                        ...filters,
                    }
                    setLocalStorageItem(storeKey, updatedFilters)
                    return { activeFilters: updatedFilters }
                })
            },
            updateActiveFilter: (key, value) => {
                set((state) => {
                    const updatedFilters = {
                        ...state.activeFilters,
                        [key]: value,
                    }
                    setLocalStorageItem(storeKey, updatedFilters)
                    return { activeFilters: updatedFilters }
                })
            },
        }
    })
}

const setLocalStorageItem = (key: string, filters: Record<string, unknown>) => {
    const payload = {
        version: STORAGE_VERSION,
        filters,
    }
    localStorage.setItem(key, JSON.stringify(payload))
}

const getLocalStorageItem = (key: string) => {
    const item = localStorage.getItem(key)

    if (!item) {
        return null
    }

    try {
        return JSON.parse(item) as {
            version: number
            filters: Record<string, unknown>
        }
    } catch {
        return null
    }
}

export const createSelectHasDefaultFilters = <
    T extends Record<string, unknown>,
>(
    defaultFilters: T
) =>
    createSelector(
        (state: FiltersStoreState<T>) => state.activeFilters,
        (activeFilters) =>
            JSON.stringify(activeFilters) === JSON.stringify(defaultFilters)
    )

export default createFiltersStore
